import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, ShieldCheck, Clock, FileText, Loader2, ExternalLink, ChevronDown, ChevronUp, User } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";

const DietitianVerification = () => {
  const { language } = useLanguage();
  const bn = language === "bn";
  const { toast } = useToast();
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [actionUserId, setActionUserId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from("profiles").select("*").in("role", ["dietitian", "doctor"]);
      if (error) {
        toast({ title: bn ? "ডেটা লোড করা যায়নি" : "Could not load applications", description: error.message, variant: "destructive" });
      }
      setPractitioners(data ?? []);
      setLoading(false);
    };
    load();
  }, []);

  const updateStatus = async (userId: string, verified: boolean) => {
    setActionUserId(userId);
    const { error } = await supabase.from("profiles").update({ verified }).eq("user_id", userId);
    if (error) {
      toast({ title: bn ? "স্ট্যাটাস আপডেট হয়নি" : "Status was not updated", description: error.message, variant: "destructive" });
      setActionUserId(null);
      return;
    }
    setPractitioners(prev => prev.map(d => d.user_id === userId ? { ...d, verified } : d));
    toast({ title: verified ? (bn ? "অনুমোদিত" : "Approved") : (bn ? "প্রত্যাখ্যাত" : "Rejected") });
    setActionUserId(null);
  };

  const getStatus = (d: any) => d.verified === true ? "approved" : "pending";
  const filtered = filter === "all" ? practitioners : practitioners.filter(d => getStatus(d) === filter);
  const counts = {
    pending: practitioners.filter(d => !d.verified).length,
    approved: practitioners.filter(d => d.verified).length,
    rejected: 0,
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid grid-cols-3 gap-4">
        {([
          { key: "pending" as const, icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10" },
          { key: "approved" as const, icon: Check, color: "text-primary", bg: "bg-primary/10" },
          { key: "rejected" as const, icon: X, color: "text-destructive", bg: "bg-destructive/10" },
        ]).map(({ key, icon: Icon, color, bg }) => (
          <Card key={key} className="cursor-pointer bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all" onClick={() => setFilter(key)}>
            <CardContent className="p-5 text-center">
              <div className={`h-10 w-10 rounded-xl ${bg} flex items-center justify-center mx-auto mb-2`}><Icon className={`h-5 w-5 ${color}`} /></div>
              <p className="text-2xl font-extrabold">{counts[key]}</p>
              <p className="text-xs text-muted-foreground capitalize">{bn ? (key === "pending" ? "মুলতুবি" : key === "approved" ? "অনুমোদিত" : "প্রত্যাখ্যাত") : key}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex gap-2">
        {(["all", "pending", "approved", "rejected"] as const).map(f => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)} className="capitalize text-xs rounded-lg">
            {f === "all" ? (bn ? "সব" : "All") : f === "pending" ? (bn ? "মুলতুবি" : "Pending") : f === "approved" ? (bn ? "অনুমোদিত" : "Approved") : (bn ? "প্রত্যাখ্যাত" : "Rejected")}
          </Button>
        ))}
      </div>

      <Card className="bg-card/60 backdrop-blur-sm border-border/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground/80 uppercase tracking-wider"><ShieldCheck className="h-4 w-4 text-primary" />{bn ? "যাচাই আবেদন" : "Verification Applications"}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">{bn ? "কোন আবেদন নেই" : "No applications found"}</p>
          ) : (
            <div className="divide-y divide-border/30">
              {filtered.map(d => {
                const status = getStatus(d);
                const isExpanded = expanded === d.user_id;
                const hasPhoto = !!d.profile_photo;
                const hasDoc = !!d.address;

                return (
                  <div key={d.id}>
                    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/10 transition-colors">
                      <div className="h-10 w-10 rounded-xl overflow-hidden bg-muted flex items-center justify-center shrink-0">
                        {hasPhoto ? (
                          <img src={d.profile_photo} alt={d.full_name} className="h-full w-full object-cover" />
                        ) : (
                          <User className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm">{d.full_name}</p>
                          <Badge variant="outline" className="text-[10px] capitalize px-1.5">{d.role}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground">{d.email}</p>
                        <p className="text-[10px] text-muted-foreground">{d.qualification ?? ""}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={status === "approved" ? "default" : "secondary"} className="text-[10px] capitalize">
                          {status === "approved" ? (bn ? "অনুমোদিত" : "Approved") : (bn ? "মুলতুবি" : "Pending")}
                        </Badge>
                        {!d.verified ? (
                          <div className="flex gap-1">
                             <Button size="sm" disabled={actionUserId === d.user_id} variant="ghost" className="h-7 w-7 p-0 text-primary hover:bg-primary/10" onClick={() => updateStatus(d.user_id, true)} title="Approve">
                               {actionUserId === d.user_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            </Button>
                             <Button size="sm" disabled={actionUserId === d.user_id} variant="ghost" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10" onClick={() => updateStatus(d.user_id, false)} title="Reject">
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : null}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setExpanded(isExpanded ? null : d.user_id)}>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 pt-2 bg-muted/10 space-y-3">
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div>
                                <span className="font-bold text-muted-foreground">{bn ? "বিশেষত্ব:" : "Specialization:"}</span>
                                <p className="mt-0.5">{d.specialization || "—"}</p>
                              </div>
                              <div>
                                <span className="font-bold text-muted-foreground">{bn ? "অভিজ্ঞতা:" : "Experience:"}</span>
                                <p className="mt-0.5">{d.experience ? `${d.experience} yrs` : "—"}</p>
                              </div>
                              <div>
                                <span className="font-bold text-muted-foreground">{bn ? "ফি:" : "Fee:"}</span>
                                <p className="mt-0.5">{d.fee ? `৳${d.fee}` : "—"}</p>
                              </div>
                              <div>
                                <span className="font-bold text-muted-foreground">{bn ? "ফোন:" : "Phone:"}</span>
                                <p className="mt-0.5">{d.phone || "—"}</p>
                              </div>
                            </div>

                            {d.bio && (
                              <div className="text-xs">
                                <span className="font-bold text-muted-foreground">{bn ? "বিবরণ:" : "Bio:"}</span>
                                <p className="mt-0.5 text-muted-foreground">{d.bio}</p>
                              </div>
                            )}

                            <div className="flex flex-wrap gap-2 pt-1">
                              {hasPhoto && (
                                <a href={d.profile_photo} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-lg px-3 py-1.5 transition-colors">
                                  <User className="h-3.5 w-3.5" />
                                  {bn ? "প্রোফাইল ছবি দেখুন" : "View Profile Photo"}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              {hasDoc && (
                                <a href={d.address} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 rounded-lg px-3 py-1.5 transition-colors">
                                  <FileText className="h-3.5 w-3.5" />
                                  {bn ? "যাচাই ডকুমেন্ট দেখুন" : "View Verification PDF"}
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              {!hasPhoto && !hasDoc && (
                                <p className="text-[11px] text-muted-foreground italic">
                                  {bn ? "কোনো ডকুমেন্ট আপলোড করা হয়নি" : "No documents uploaded (signed up before this feature)"}
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DietitianVerification;
